import { apiRequest } from '../axiosConfig';
import {
  GASettings,
  GASettingInput,
  GADailyMetric,
  GATrafficSource,
  GASummary,
  GASyncStatus,
  GAMetricsParams,
  GASyncParams,
  GASettingsResponse,
  GAMetricsResponse,
  GATrafficSourcesResponse,
  GASummaryResponse,
  GASyncStatusResponse,
  GASyncTriggerResponse,
} from '../../../types/analytics';

export interface AnalyticsService {
  getSettings(productId: number): Promise<GASettings | null>;
  saveSettings(productId: number, data: GASettingInput): Promise<GASettings>;
  deleteSettings(productId: number): Promise<void>;
  getMetrics(productId: number, params?: GAMetricsParams): Promise<GADailyMetric[]>;
  getTrafficSources(productId: number, params?: GAMetricsParams): Promise<GATrafficSource[]>;
  getSummary(productId: number, params?: GAMetricsParams): Promise<GASummary>;
  syncData(productId: number, params?: GASyncParams): Promise<{ message: string; jobs_dispatched: number }>;
  getSyncStatus(productId: number): Promise<GASyncStatus>;
}

class AnalyticsServiceImpl implements AnalyticsService {
  /**
   * Get GA settings for a product
   */
  async getSettings(productId: number): Promise<GASettings | null> {
    try {
      const response = await apiRequest.get<GASettingsResponse>(
        `/knowledge/products/${productId}/analytics/settings`
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Save or update GA settings for a product
   */
  async saveSettings(productId: number, data: GASettingInput): Promise<GASettings> {
    const response = await apiRequest.post<GASettingsResponse>(
      `/knowledge/products/${productId}/analytics/settings`,
      data
    );
    return response.data;
  }

  /**
   * Delete GA settings for a product
   */
  async deleteSettings(productId: number): Promise<void> {
    await apiRequest.delete(
      `/knowledge/products/${productId}/analytics/settings`
    );
  }

  /**
   * Get daily metrics for a product
   */
  async getMetrics(productId: number, params?: GAMetricsParams): Promise<GADailyMetric[]> {
    const response = await apiRequest.get<GAMetricsResponse>(
      `/knowledge/products/${productId}/analytics/metrics`,
      { params }
    );
    return response.data;
  }

  /**
   * Get traffic sources for a product
   */
  async getTrafficSources(productId: number, params?: GAMetricsParams): Promise<GATrafficSource[]> {
    const response = await apiRequest.get<GATrafficSourcesResponse>(
      `/knowledge/products/${productId}/analytics/traffic-sources`,
      { params }
    );
    return response.data;
  }

  /**
   * Get summary statistics for a product
   */
  async getSummary(productId: number, params?: GAMetricsParams): Promise<GASummary> {
    const response = await apiRequest.get<GASummaryResponse>(
      `/knowledge/products/${productId}/analytics/summary`,
      { params }
    );
    return response.data;
  }

  /**
   * Trigger manual sync of GA data for a date range
   */
  async syncData(productId: number, params?: GASyncParams): Promise<{ message: string; jobs_dispatched: number }> {
    const response = await apiRequest.post<GASyncTriggerResponse>(
      `/knowledge/products/${productId}/analytics/sync`,
      params
    );
    return response.data;
  }

  /**
   * Get current sync status
   */
  async getSyncStatus(productId: number): Promise<GASyncStatus> {
    const response = await apiRequest.get<GASyncStatusResponse>(
      `/knowledge/products/${productId}/analytics/sync-status`
    );
    return response.data;
  }
}

export const analyticsService = new AnalyticsServiceImpl();
